/* RUQAY — progressive interactions & motion */
(() => {
  const q = (s, c = document) => c.querySelector(s);
  const qa = (s, c = document) => [...c.querySelectorAll(s)];
  const body = document.body;
  const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)');

  const nav = q('[data-mobile-nav]');
  const search = q('[data-search-bar]');
  const drawer = q('[data-cart-drawer]');
  const header = q('#site-header');

  /* ---------- Mobile nav ---------- */
  const navToggle = q('[data-nav-toggle]');
  const navBackdrop = q('[data-nav-backdrop]');
  // Viewport-level layers avoid header backdrop-filter containing blocks and announcement overlap.
  if (navBackdrop && nav) body.append(navBackdrop, nav);
  const closeNav = (restoreFocus = true) => {
    if (!nav || nav.hidden) return;
    nav.hidden = true;
    if (navBackdrop) navBackdrop.hidden = true;
    navToggle?.setAttribute('aria-expanded', 'false');
    if (restoreFocus) navToggle?.focus({ preventScroll: true });
  };
  navToggle?.addEventListener('click', (e) => {
    if (!nav) return;
    closeCart();
    closeSearch(false);
    nav.hidden = false;
    if (navBackdrop) navBackdrop.hidden = false;
    e.currentTarget.setAttribute('aria-expanded', 'true');
    q('[data-nav-close]', nav)?.focus({ preventScroll: true });
  });
  q('[data-nav-close]')?.addEventListener('click', () => closeNav());
  navBackdrop?.addEventListener('click', () => closeNav());
  const trapFocus = (event, panel) => {
    if (event.key !== 'Tab') return;
    const controls = qa('a[href], button:not([disabled]), input:not([disabled]), [tabindex="0"]', panel).filter((element) => element.getClientRects().length);
    const first = controls[0], last = controls[controls.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
  };
  nav?.addEventListener('keydown', (event) => trapFocus(event, nav));

  /* ---------- Header search ---------- */
  const closeSearch = (restoreFocus = true) => {
    if (!search || search.hidden) return;
    search.hidden = true;
    q('[data-search-toggle]')?.setAttribute('aria-expanded', 'false');
    if (restoreFocus) q('[data-search-toggle]')?.focus({ preventScroll: true });
  };
  q('[data-search-close]')?.addEventListener('click', () => closeSearch());
  q('[data-search-toggle]')?.addEventListener('click', () => {
    if (!search) return;
    closeNav(false);
    search.hidden = !search.hidden;
    q('[data-search-toggle]')?.setAttribute('aria-expanded', String(!search.hidden));
    if (!search.hidden) q('input', search)?.focus();
  });

  /* ---------- Cart drawer ---------- */
  let cartReturnFocus = null;
  const openCart = () => {
    if (!drawer) return;
    cartReturnFocus = document.activeElement;
    closeNav(false);
    closeSearch(false);
    drawer?.classList.add('is-open');
    drawer?.setAttribute('aria-hidden', 'false');
    drawer.setAttribute('role', 'dialog');
    drawer.setAttribute('aria-modal', 'true');
    body.style.overflow = 'hidden';
    q('.cart-drawer__panel [data-cart-close]', drawer)?.focus({ preventScroll: true });
  };
  const closeCart = () => {
    if (!drawer?.classList.contains('is-open')) return;
    drawer?.classList.remove('is-open');
    drawer?.setAttribute('aria-hidden', 'true');
    body.style.overflow = '';
    cartReturnFocus?.focus({ preventScroll: true });
  };
  drawer?.addEventListener('keydown', (event) => trapFocus(event, q('.cart-drawer__panel', drawer)));
  qa('[data-cart-open]').forEach((x) => x.addEventListener('click', openCart));
  qa('[data-cart-close]').forEach((x) => x.addEventListener('click', closeCart));

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      closeCart();
      closeNav();
      closeSearch();
    }
  });

  /* ---------- Quantity steppers ---------- */
  qa('[data-quantity-minus]').forEach((b) =>
    b.addEventListener('click', () => {
      const i = q('input', b.parentElement);
      i.value = Math.max(1, (+i.value || 1) - 1);
    })
  );
  qa('[data-quantity-plus]').forEach((b) =>
    b.addEventListener('click', () => {
      const i = q('input', b.parentElement);
      i.value = (+i.value || 1) + 1;
    })
  );

  /* ---------- AJAX add-to-cart + bubble pulse ---------- */
  const pulseBubble = (el) => {
    el.classList.remove('is-pulsing');
    void el.offsetWidth;
    el.classList.add('is-pulsing');
  };

  qa('[data-cart-form]').forEach((form) =>
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      if (form.getAttribute('aria-busy') === 'true') return;
      const buttons = [...form.elements].filter((element) => element.type === 'submit');
      const disabledStates = buttons.map((button) => button.disabled);
      form.setAttribute('aria-busy', 'true');
      buttons.forEach((button) => { button.disabled = true; });
      try {
        const response = await fetch(window.yoma.routes.cart_add, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: new FormData(form)
        });
        if (!response.ok) throw new Error('cart');
        const cart = await fetch(`${window.yoma.routes.cart}.js`).then((r) => r.json());
        qa('[data-cart-count]').forEach((x) => {
          x.textContent = cart.item_count;
          x.hidden = cart.item_count === 0;
          pulseBubble(x);
        });
        window.location.href = window.yoma.routes.cart;
      } catch (error) {
        form.submit();
      } finally {
        form.removeAttribute('aria-busy');
        buttons.forEach((button, index) => { button.disabled = disabledStates[index]; });
      }
    })
  );

  /* ---------- Staggered reveal (content stays visible if JS cannot run) ---------- */
  const revealObserver =
    'IntersectionObserver' in window && !reduceMotion.matches
      ? new IntersectionObserver(
          (entries) =>
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                entry.target.classList.add('is-visible');
                revealObserver.unobserve(entry.target);
              }
            }),
          { threshold: 0.12, rootMargin: '0px 0px -4% 0px' }
        )
      : null;

  const initReveals = (root = document) => {
    qa('[data-stagger]', root).forEach((group) => {
      qa('.reveal', group).forEach((element, index) => {
        element.style.setProperty('--reveal-delay', `${Math.min(index * 95, 570)}ms`);
      });
    });

    qa('.reveal', root).forEach((element) => {
      if (!revealObserver || element.classList.contains('is-visible')) {
        element.classList.add('is-visible');
        return;
      }
      element.classList.add('is-reveal-pending');
      revealObserver.observe(element);
    });
  };

  initReveals();
  document.addEventListener('shopify:section:load', (event) => initReveals(event.target));

  /* ---------- Announcement rotation ---------- */
  const announcements = qa('.announcement-bar__item');
  if (announcements.length > 1 && !reduceMotion.matches) {
    let i = 0;
    setInterval(() => {
      announcements[i].classList.add('is-hidden');
      i = (i + 1) % announcements.length;
      announcements[i].classList.remove('is-hidden');
    }, 4500);
  }

  /* ---------- Header scroll state ---------- */
  if (header) {
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        header.classList.toggle('is-scrolled', window.scrollY > 24);
        ticking = false;
      });
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  /* ---------- Product gallery: user-controlled, RTL-aware ---------- */
  qa('[data-product-gallery]').forEach((gallery) => {
    const slides = qa('[data-product-media]', gallery);
    if (slides.length < 2) return;
    const thumbs = qa('[data-gallery-thumb]', gallery);
    const status = q('[data-gallery-status]', gallery);
    let current = 0;
    const show = (index) => {
      current = (index + slides.length) % slides.length;
      slides.forEach((slide, i) => {
        const active = i === current;
        if (!active && !slide.hidden) {
          qa('video', slide).forEach((video) => video.pause());
          // Reload an outgoing embed to stop playback without a provider SDK.
          qa('iframe', slide).forEach((frame) => { frame.src = frame.src; });
        }
        slide.hidden = !active;
        slide.classList.toggle('is-active', active);
      });
      thumbs.forEach((thumb, i) => thumb.setAttribute('aria-current', String(i === current)));
      if (status) status.textContent = `${current + 1} / ${slides.length}`;
    };
    thumbs.forEach((thumb, index) => thumb.addEventListener('click', () => show(index)));
    q('[data-gallery-previous]', gallery)?.addEventListener('click', () => show(current - 1));
    q('[data-gallery-next]', gallery)?.addEventListener('click', () => show(current + 1));
    // Horizontal touch swipes only; native vertical scrolling and pinch zoom stay available.
    const stage = q('.product-main__stage', gallery);
    let gesture = null;
    stage?.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'touch' || !event.isPrimary || !event.target.closest('img')) return;
      gesture = { id: event.pointerId, x: event.clientX, y: event.clientY };
    }, { passive: true });
    stage?.addEventListener('pointerup', (event) => {
      if (!gesture || event.pointerId !== gesture.id) return;
      const dx = event.clientX - gesture.x, dy = event.clientY - gesture.y;
      gesture = null;
      if (Math.abs(dx) < 45 || Math.abs(dx) < Math.abs(dy) * 1.4) return;
      const rtl = getComputedStyle(gallery).direction === 'rtl';
      show(current + ((dx > 0) === rtl ? 1 : -1));
    }, { passive: true });
    stage?.addEventListener('pointercancel', () => { gesture = null; }, { passive: true });
    gallery.addEventListener('keydown', (event) => {
      // Native video/model controls keep their own arrow-key behavior.
      if (event.target !== gallery && !event.target.closest('[data-gallery-thumb], [data-gallery-previous], [data-gallery-next]')) return;
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      const rtl = getComputedStyle(gallery).direction === 'rtl';
      if (event.key === 'Home') show(0);
      else if (event.key === 'End') show(slides.length - 1);
      else show(current + ((event.key === 'ArrowLeft') === rtl ? 1 : -1));
      if (event.target.closest('[data-gallery-thumb]')) thumbs[current]?.focus();
    });
    show(0);
    qa('[data-gallery-controls], [data-gallery-thumbs]', gallery).forEach((element) => { element.hidden = false; });
  });

  /* ---------- Sticky purchase bar, only after passing the form ---------- */
  qa('[data-product-root]').forEach((root) => {
    const form = q('#ProductForm', root);
    const bar = q('[data-sticky-atc]', root);
    if (!form || !bar) return;
    const syncSticky = () => {
      const visible = form.getBoundingClientRect().bottom <= 0;
      bar.hidden = !visible;
      bar.inert = !visible;
      bar.setAttribute('aria-hidden', String(!visible));
      bar.classList.toggle('is-visible', visible);
    };
    let stickyTicking = false;
    const requestStickySync = () => {
      if (stickyTicking) return;
      stickyTicking = true;
      requestAnimationFrame(() => {
        syncSticky();
        stickyTicking = false;
      });
    };
    const stickyObserver = 'IntersectionObserver' in window
      ? new IntersectionObserver(requestStickySync, { threshold: [0, 1] })
      : null;
    stickyObserver?.observe(form);
    window.addEventListener('scroll', requestStickySync, { passive: true });
    window.addEventListener('resize', requestStickySync);
    syncSticky();
    document.addEventListener('shopify:section:unload', (event) => {
      if (!event.target.contains(root)) return;
      stickyObserver?.disconnect();
      window.removeEventListener('scroll', requestStickySync);
      window.removeEventListener('resize', requestStickySync);
    });
  });

  /* ---------- Predictive search ---------- */
  const predictiveInput = q('[data-predictive-input]');
  const predictiveResults = q('[data-predictive-results]');
  if (predictiveInput && predictiveResults && window.yoma?.routes?.predictive_search) {
    let debounce;
    const hide = () => {
      predictiveResults.hidden = true;
      predictiveResults.innerHTML = '';
    };
    predictiveInput.addEventListener('input', () => {
      clearTimeout(debounce);
      const term = predictiveInput.value.trim();
      if (term.length < 2) {
        hide();
        return;
      }
      debounce = setTimeout(async () => {
        try {
          const url = `${window.yoma.routes.predictive_search}.json?q=${encodeURIComponent(term)}&resources[type]=product&resources[limit]=4`;
          const data = await fetch(url).then((r) => r.json());
          const products = data?.resources?.results?.products || [];
          if (!products.length) {
            const empty = document.createElement('p');
            empty.className = 'predictive-search__empty';
            empty.textContent = `${term} — لا توجد نتائج`;
            predictiveResults.replaceChildren(empty);
          } else {
            predictiveResults.innerHTML = products
              .map(
                (p) => `
              <a class="predictive-search__item" href="${p.url}">
                ${p.featured_image?.url ? `<img src="${p.featured_image.url}&width=92" alt="" width="46" height="46">` : ''}
                <span><strong>${p.title}</strong><span>${p.price}</span></span>
              </a>`
              )
              .join('');
          }
          predictiveResults.hidden = false;
        } catch (error) {
          hide();
        }
      }, 220);
    });
    document.addEventListener('click', (e) => {
      if (!search.contains(e.target)) hide();
    });
  }
})();
